import { useEffect, useMemo, useRef } from "react";
import { AnimatePresence } from "framer-motion";
import { ModalShell } from "@/components/ui/ModalShell";
import { Button } from "@/components/ui/Button";
import { X } from "lucide-react";
import { useFocusTrap } from "@/hooks/useFocusTrap";
import { cn } from "@/lib/cn";
import { useTranslation } from "@/i18n/I18nProvider";
import { useTaxonomy } from "@/data/TaxonomyProvider";
import { labelFor } from "@/lib/taxonomyLabels";
import { hasToken, type FilterToken } from "@/lib/feedFilter";
import type { Tag, TagCategory } from "@/types/taxonomy";

export interface FilterModalProps {
  open: boolean;
  onClose: () => void;
  active: FilterToken[];
  onToggle: (token: FilterToken) => void;
  onClear: () => void;
  /** Live count of venues matching the current selection (footer button). */
  resultCount: number;
}

const TAG_GROUP_ORDER: (TagCategory | "other")[] = ["setting", "vibe", "dietary", "other"];

/**
 * Custom-filter popup: pick any cuisines + tags. Toggles the shared active-token
 * set live (the feed behind updates as you go); the footer button just closes.
 * Reuses the crisp-edge modal shell + spring entrance from the offers popup.
 */
export function FilterModal({ open, onClose, active, onToggle, onClear, resultCount }: FilterModalProps) {
  const { t, lang } = useTranslation();
  const { cuisines, tags } = useTaxonomy();
  const panelRef = useRef<HTMLDivElement>(null);
  useFocusTrap(panelRef, open);

  // Escape to close + lock background scroll while open (same as the offers popup).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = previous;
    };
  }, [open, onClose]);

  const tagGroups = useMemo(() => {
    const by: Record<string, Tag[]> = {};
    for (const tg of tags) (by[tg.category ?? "other"] ??= []).push(tg);
    return TAG_GROUP_ORDER.map((cat) => ({ cat, items: by[cat] ?? [] })).filter((g) => g.items.length);
  }, [tags]);

  return (
    <AnimatePresence>
      {open && (
        <ModalShell
          key="filter-backdrop"
          align="sheet"
          onBackdropClick={onClose}
          aria-label={t("HOME_FILTERS_TITLE")}
          panelRef={panelRef}
          panelClass="flex max-h-[88dvh] max-w-md flex-col overflow-hidden"
        >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-white/10 light:border-black/10 px-5 py-4">
              <h2 className="font-display text-lg tracking-tight text-zinc-50 light:text-zinc-900">{t("HOME_FILTERS_TITLE")}</h2>
              <button
                type="button"
                onClick={onClose}
                aria-label={t("CLOSE")}
                className="grid h-9 w-9 place-items-center rounded-full border border-white/15 light:border-black/15 bg-zinc-900/80 light:bg-white/80 text-zinc-300 light:text-zinc-700 transition hover:border-white/50 light:hover:border-black/40 hover:text-zinc-50 light:hover:text-zinc-900"
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 space-y-5 overflow-y-auto px-5 py-4">
              <Section title={t("HOME_FILTERS_CUISINES")}>
                {cuisines.map((c) => {
                  const token: FilterToken = { kind: "cuisine", slug: c.slug };
                  return (
                    <Chip key={c.slug} on={hasToken(active, token)} onClick={() => onToggle(token)}>
                      {labelFor(c, lang)}
                    </Chip>
                  );
                })}
              </Section>

              <Section title={t("HOME_FILTERS_TAGS")}>
                {tagGroups.flatMap((g) =>
                  g.items.map((tg) => {
                    const token: FilterToken = { kind: "tag", slug: tg.slug };
                    return (
                      <Chip key={tg.slug} on={hasToken(active, token)} onClick={() => onToggle(token)}>
                        {labelFor(tg, lang)}
                      </Chip>
                    );
                  }),
                )}
              </Section>
            </div>

            {/* Footer */}
            <div className="flex items-center gap-3 border-t border-white/10 light:border-black/10 px-5 py-4">
              <button
                type="button"
                onClick={onClear}
                disabled={active.length === 0}
                className="text-sm font-medium text-zinc-400 light:text-zinc-600 transition hover:text-zinc-100 light:hover:text-zinc-900 disabled:opacity-40"
              >
                {t("HOME_FILTERS_CLEAR")}
              </button>
              <Button onClick={onClose} className="ml-auto px-5">
                {t("HOME_FILTERS_SHOW", { n: resultCount })}
              </Button>
            </div>
        </ModalShell>
      )}
    </AnimatePresence>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <p className="font-mono text-3xs uppercase tracking-[0.2em] text-dim light:text-zinc-600">{title}</p>
      <div className="flex flex-wrap gap-1.5">{children}</div>
    </div>
  );
}

function Chip({ on, onClick, children }: { on: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      className={cn(
        "rounded-full border px-3 py-1.5 text-xs font-medium transition sm:py-1",
        on
          ? "border-white/30 light:border-black/25 bg-zinc-100 light:bg-zinc-900 text-zinc-900 light:text-zinc-50"
          : "border-white/[0.12] light:border-black/10 bg-zinc-900/60 light:bg-white/70 text-zinc-300 light:text-zinc-700 hover:border-white/30 light:hover:border-black/30 hover:text-zinc-100 light:hover:text-zinc-900",
      )}
    >
      {children}
    </button>
  );
}
